import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {string | undefined} */
  #pendingMediaId;

  /** @type {string | undefined} */
  #pendingMediaSrc;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // pointerdown = immediate response (before change / fetch)
    target?.addEventListener('pointerdown', this.#handlePointerPreview, { signal, capture: true });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#preloadVariantImages(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Preview media as soon as the user presses a color swatch (no network wait).
   * @param {PointerEvent} event
   */
  #handlePointerPreview = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const label = event.target.closest('label.variant-option__button-label--has-swatch');
    if (!label) return;

    const input = label.querySelector('input[data-media-src], input[data-media-id]');
    if (!(input instanceof HTMLInputElement)) return;
    if (input.checked || input.disabled || input.getAttribute('aria-disabled') === 'true') return;

    this.#applyMediaPreview(input.dataset.mediaId, input.dataset.mediaSrc);
  };

  /**
   * Instantly preview the selected variant media (before the section fetch finishes).
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const mediaId = event.detail?.resource?.mediaId;
    const mediaSrc = event.detail?.resource?.mediaSrc;
    if (!mediaId && !mediaSrc) return;

    this.#applyMediaPreview(mediaId, mediaSrc);
  };

  /**
   * @param {string | undefined} mediaId
   * @param {string | undefined} mediaSrc
   */
  #applyMediaPreview(mediaId, mediaSrc) {
    const nextId = mediaId ? String(mediaId) : undefined;
    if (nextId && this.#pendingMediaId === nextId) return;
    if (!nextId && mediaSrc && this.#pendingMediaSrc === mediaSrc) return;

    this.#pendingMediaId = nextId;
    this.#pendingMediaSrc = mediaSrc;

    // Fast path: media already in the gallery
    if (nextId) {
      const mediaEl = this.querySelector(`[data-media-id="${nextId}"]`);
      const slide = mediaEl?.closest('slideshow-slide');
      if (slide && this.slideshow) {
        const slideId = slide.getAttribute('slide-id');
        if (slideId) {
          this.slideshow.select({ id: slideId }, undefined, { animate: false });
          return;
        }
      }
    }

    // Instant main-image swap (do not wait for decode / network)
    if (mediaSrc) {
      this.#optimisticSwapImage(mediaSrc);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    // Prefer the gallery from the same product section (avoids grabbing a different product's gallery)
    const sectionId = this.closest('.shopify-section')?.id;
    let newMediaGallery =
      (sectionId ? source.getElementById(sectionId)?.querySelector('media-gallery') : null) ||
      source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const featuredMediaId = event.detail.resource?.featured_media?.id ?? this.#pendingMediaId;
    if (featuredMediaId != null) {
      this.#preferFeaturedMedia(newMediaGallery, featuredMediaId);
    }

    const alreadyShowing =
      this.#pendingMediaId && featuredMediaId != null && String(featuredMediaId) === this.#pendingMediaId;

    const commit = () => {
      // Avoid tearing down if the incoming gallery is effectively the same featured media
      // and we already painted it — still replace so thumbnails catch up, but off the click path.
      this.replaceWith(newMediaGallery);
      this.#pendingMediaId = undefined;
      this.#pendingMediaSrc = undefined;
    };

    if (alreadyShowing) {
      // Main image already correct — refresh full gallery when the browser is idle
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(commit, { timeout: 300 });
      } else {
        setTimeout(commit, 0);
      }
    } else {
      commit();
    }
  };

  /**
   * Ensures the variant's featured media is the starting slide before the gallery is inserted.
   *
   * @param {Element} gallery
   * @param {number|string} featuredMediaId
   */
  #preferFeaturedMedia(gallery, featuredMediaId) {
    const mediaEl = gallery.querySelector(`[data-media-id="${featuredMediaId}"]`);
    if (!mediaEl) return;

    const slide = mediaEl.closest('slideshow-slide');
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slide || !slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const index = slides.indexOf(slide);
    if (index > 0) {
      slideshow.setAttribute('initial-slide', String(index));
    }
  }

  /**
   * Instantly swap the main product image to the variant preview URL.
   * @param {string} src
   */
  #optimisticSwapImage(src) {
    const img = this.#getMainImage();
    if (!img || !src) return;

    // Sync paint — cached images appear immediately after preload
    img.src = src;
    img.srcset = '';
    img.removeAttribute('srcset');
    img.sizes = '100vw';
    img.loading = 'eager';
    img.decoding = 'sync';
  }

  /**
   * Preload all color variant featured images so the first click is instant.
   * @param {Element | null | undefined} scope
   */
  #preloadVariantImages(scope) {
    const root = scope || this.closest('.shopify-section') || document;
    const srcs = new Set();

    root.querySelectorAll('variant-picker input[data-media-src]').forEach((input) => {
      if (input instanceof HTMLElement && input.dataset.mediaSrc) {
        srcs.add(input.dataset.mediaSrc);
      }
    });

    srcs.forEach((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
  }

  /**
   * @returns {HTMLImageElement | null}
   */
  #getMainImage() {
    const activeSlide =
      this.querySelector('slideshow-slide[aria-hidden="false"]') ||
      this.querySelector('slideshow-slide') ||
      this.querySelector('.product-media-container');

    return (
      /** @type {HTMLImageElement | null} */ (activeSlide?.querySelector('img')) ||
      this.querySelector('.product-media img') ||
      this.querySelector('img')
    );
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
